import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../Auth/AuthContext";
import api from "../../utils/api";
import Navigation from "../Navigation/navigation";

const EditStaffInfo = () => {
  const { staffId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [staffData, setStaffData] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [referenceData, setReferenceData] = useState({
    genders: [],
    maritalStatuses: [],
    religions: [],
    educations: [],
    ethnicities: [],
    nrcTypes: [],
    nrcAreas: [],
    nrcTownships: [],
  });

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate("/login");
      return;
    }
    if (staffId) {
      fetchStaffData();
      fetchReferenceData();
    }
  }, [staffId, isAuthenticated, user, navigate]);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/staff/${staffId}`);
      setStaffData(response.data);
      
      const cleanedFormData = {
        ...response.data,
        nrc_type_id: response.data.nrc_type_id ? String(response.data.nrc_type_id) : "2",
      };

      // Clean up conflicting ID data based on selected nrc_type_id
      const nrcTypeId = cleanedFormData.nrc_type_id;
      
      if (nrcTypeId === "1") {
        // Myanmar NRC selected - clear other ID fields
        cleanedFormData.other_id_number = "";
        cleanedFormData.other_id_description = "";
      } else if (nrcTypeId === "3") {
        // Other ID selected - clear Myanmar NRC fields
        cleanedFormData.myanmar_nrc_area_code = "";
        cleanedFormData.myanmar_nrc_township_code = "";
        cleanedFormData.myanmar_nrc_type = "";
        cleanedFormData.myanmar_nrc_number = "";
      } else {
        // No ID selected - clear all ID fields
        cleanedFormData.myanmar_nrc_area_code = "";
        cleanedFormData.myanmar_nrc_township_code = "";
        cleanedFormData.myanmar_nrc_type = "";
        cleanedFormData.myanmar_nrc_number = "";
        cleanedFormData.other_id_number = "";
        cleanedFormData.other_id_description = "";
      }

      setFormData(cleanedFormData);
    } catch (error) {
      console.error("Error fetching staff data:", error);
      setError("Error retrieving staff information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [
        genders,
        maritalStatuses,
        religions,
        educations,
        ethnicities,
        nrcTypes,
        nrcAreas,
        nrcTownships,
      ] = await Promise.all([
        api.get("/api/reference/genders"),
        api.get("/api/reference/marital-status"),
        api.get("/api/reference/religions"),
        api.get("/api/reference/educations"),
        api.get("/api/reference/ethnicities"),
        api.get("/api/reference/nrc-types"),
        api.get("/api/reference/nrc-areas"),
        api.get("/api/reference/nrc-townships"),
      ]);

      // Helper function to extract data from response (handles both direct arrays and wrapped responses)
      const extractData = (response) => {
        if (Array.isArray(response.data)) {
          return response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          return response.data.data;
        } else if (
          response.data &&
          response.data.success &&
          Array.isArray(response.data.data)
        ) {
          return response.data.data;
        }
        return [];
      };

      setReferenceData({
        genders: extractData(genders),
        maritalStatuses: extractData(maritalStatuses),
        religions: extractData(religions),
        educations: extractData(educations),
        ethnicities: extractData(ethnicities),
        nrcTypes: extractData(nrcTypes),
        nrcAreas: extractData(nrcAreas),
        nrcTownships: extractData(nrcTownships),
      });
    } catch (error) {
      console.error("Error fetching reference data:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  // Handle same address checkbox
  const handleSameAddressChange = (checked) => {
    if (checked && formData.permanent_address) {
      handleInputChange("current_address", formData.permanent_address);
    } else if (!checked) {
      handleInputChange("current_address", "");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      // Clean the form data based on selected ID type before submitting
      const cleanedData = { ...formData };
      const nrcTypeId = cleanedData.nrc_type_id;

      if (nrcTypeId === "1") {
        // Myanmar NRC selected - clear other ID fields in database
        cleanedData.other_id_number = null;
        cleanedData.other_id_description = null;
      } else if (nrcTypeId === "3") {
        // Other ID selected - clear Myanmar NRC fields in database
        cleanedData.myanmar_nrc_area_code = null;
        cleanedData.myanmar_nrc_township_code = null;
        cleanedData.myanmar_nrc_type = null;
        cleanedData.myanmar_nrc_number = null;
      } else {
        // No ID selected - clear ALL ID fields in database
        cleanedData.myanmar_nrc_area_code = null;
        cleanedData.myanmar_nrc_township_code = null;
        cleanedData.myanmar_nrc_type = null;
        cleanedData.myanmar_nrc_number = null;
        cleanedData.other_id_number = null;
        cleanedData.other_id_description = null;
      }

      await api.put(`/api/staff/${staffId}/basic-info`, cleanedData);
      setSuccessMessage("Staff information updated successfully!");
      setTimeout(() => {
        navigate(`/edit-staff/${staffId}`);
      }, 2000);
    } catch (error) {
      console.error("Error updating staff info:", error);
      setError(
        error.response?.data?.message ||
          "Error updating staff information. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/edit-staff/${staffId}`);
  };

  if (loading && !staffData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h4 className="text-xl font-semibold text-gray-800">
              Loading Staff Information...
            </h4>
          </div>
        </div>
      </div>
    );
  }

  if (error && !staffData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-red-500 mb-4" style={{ fontSize: "3rem" }}>
              ❌
            </div>
            <h4 className="text-xl font-semibold text-red-600 mb-4">
              Error Loading Staff Data
            </h4>
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <strong>Error:</strong> {error}
            </div>
            <button
              onClick={handleCancel}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              Back to Staff Details
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-4xl mx-auto p-5 pt-20">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-blue-800 mb-2">
                👤 Edit Staff Information
              </h1>
              <p className="text-gray-600">
                Editing personal details for:{" "}
                <strong>{staffData?.fullname}</strong> (ID:{" "}
                {staffData?.staff_id})
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Success and Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <h2 className="text-xl font-semibold">Staff Information Form</h2>
            <p className="text-blue-100 mt-1">
              Update personal details, contact information, and identification
            </p>
          </div>

          <div className="p-6 space-y-8">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Basic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.staff_id || ""}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Staff ID cannot be changed
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullname || ""}
                    onChange={(e) =>
                      handleInputChange("fullname", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formatDateForInput(formData.date_of_birth)}
                    onChange={(e) =>
                      handleInputChange("date_of_birth", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.gender_id || ""}
                    onChange={(e) =>
                      handleInputChange("gender_id", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Gender</option>
                    {referenceData.genders.map((gender) => (
                      <option key={gender.id} value={gender.id}>
                        {gender.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marital Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.maritalstatus_id || ""}
                    onChange={(e) =>
                      handleInputChange("maritalstatus_id", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Marital Status</option>
                    {referenceData.maritalStatuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Religion <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.religion_id || ""}
                    onChange={(e) =>
                      handleInputChange("religion_id", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Religion</option>
                    {referenceData.religions.map((religion) => (
                      <option key={religion.id} value={religion.id}>
                        {religion.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ethnicity
                  </label>
                  <select
                    value={formData.ethnicity_id || ""}
                    onChange={(e) => {
                      const selectedEthnicityId = e.target.value;
                      handleInputChange("ethnicity_id", selectedEthnicityId);
                      
                      // Clear other_ethnicity if user selects a specific ethnicity (not "Other")
                      if (selectedEthnicityId) {
                        const selectedEthnicity = referenceData.ethnicities.find(
                          (eth) => String(eth.id) === String(selectedEthnicityId)
                        );
                        if (selectedEthnicity && !selectedEthnicity.ethnicity_name?.toLowerCase().includes("other")) {
                          handleInputChange("other_ethnicity", "");
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Ethnicity</option>
                    {referenceData.ethnicities.map((ethnicity) => (
                      <option key={ethnicity.id} value={ethnicity.id}>
                        {ethnicity.ethnicity_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Show Other Ethnicity field when needed */}
                {formData.ethnicity_id &&
                  // Check if the selected ethnicity needs "other" specification
                  (referenceData.ethnicities
                    .find((eth) => String(eth.id) === String(formData.ethnicity_id))
                    ?.ethnicity_name?.toLowerCase()
                    .includes("other") ||
                    formData.other_ethnicity) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Other Ethnicity (Please specify)
                      </label>
                      <input
                        type="text"
                        value={formData.other_ethnicity || ""}
                        onChange={(e) =>
                          handleInputChange("other_ethnicity", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Please specify ethnicity"
                      />
                    </div>
                  )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Father's Name
                  </label>
                  <input
                    type="text"
                    value={formData.father_name || ""}
                    onChange={(e) =>
                      handleInputChange("father_name", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter father's name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mother's Name
                  </label>
                  <input
                    type="text"
                    value={formData.mother_name || ""}
                    onChange={(e) =>
                      handleInputChange("mother_name", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter mother's name"
                  />
                </div>
              </div>
            </div>

            {/* Education Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Education Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Education Level <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.education_id || ""}
                    onChange={(e) =>
                      handleInputChange("education_id", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select Education Level</option>
                    {referenceData.educations.map((education) => (
                      <option key={education.id} value={education.id}>
                        {education.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Education Details
                  </label>
                  <textarea
                    value={formData.details_education || ""}
                    onChange={(e) =>
                      handleInputChange("details_education", e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter additional education details, specializations, etc."
                  />
                </div>
              </div>
            </div>

            {/* National ID Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                National ID Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.nrc_type_id || "2"}
                  onChange={(e) => {
                    handleInputChange("nrc_type_id", e.target.value);
                    
                    // Clear all ID fields first
                    handleInputChange("myanmar_nrc_area_code", "");
                    handleInputChange("myanmar_nrc_township_code", "");
                    handleInputChange("myanmar_nrc_type", "");
                    handleInputChange("myanmar_nrc_number", "");
                    handleInputChange("other_id_number", "");
                    handleInputChange("other_id_description", "");
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {referenceData.nrcTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Myanmar NRC Fields */}
              {(formData.nrc_type_id === "1" || formData.nrc_type_id === 1) && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-blue-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Area Code <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.myanmar_nrc_area_code || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "myanmar_nrc_area_code",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Area</option>
                      {referenceData.nrcAreas.map((area) => (
                        <option key={area.area_code} value={area.area_code}>
                          {area.area_code} - {area.area_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Township Code <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.myanmar_nrc_township_code || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "myanmar_nrc_township_code",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={!formData.myanmar_nrc_area_code}
                    >
                      <option value="">Select Township</option>
                      {referenceData.nrcTownships
                        .filter(
                          (township) =>
                            String(township.area_code) ===
                            String(formData.myanmar_nrc_area_code)
                        )
                        .map((township) => (
                          <option
                            key={township.township_code}
                            value={township.township_code}
                          >
                            {township.township_code}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.myanmar_nrc_type || ""}
                      onChange={(e) =>
                        handleInputChange("myanmar_nrc_type", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Type</option>
                      <option value="N">နိုင် (N)</option>
                      <option value="A">ဧည့် (A)</option>
                      <option value="P">ပြု (P)</option>
                      <option value="F">သာ (F)</option>
                  
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.myanmar_nrc_number || ""}
                      onChange={(e) =>
                        handleInputChange("myanmar_nrc_number", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="6-digit number"
                      maxLength="6"
                    />
                  </div>
                </div>
              )}

              {/* Other ID Fields */}
              {(formData.nrc_type_id === "3" || formData.nrc_type_id === 3) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-purple-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID Type Description
                    </label>
                    <input
                      type="text"
                      value={formData.other_id_description || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "other_id_description",
                          e.target.value
                        )
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., Thai ID, KNU ID, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID Number
                    </label>
                    <input
                      type="text"
                      value={formData.other_id_number || ""}
                      onChange={(e) =>
                        handleInputChange("other_id_number", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter ID number"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Contact Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email || ""}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone_number || ""}
                    onChange={(e) =>
                      handleInputChange("phone_number", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter phone number"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Permanent Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.permanent_address || ""}
                    onChange={(e) =>
                      handleInputChange("permanent_address", e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter permanent address"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.current_address || ""}
                    onChange={(e) =>
                      handleInputChange("current_address", e.target.value)
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter current address"
                    required
                  />
                  <div className="mt-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={
                          formData.current_address ===
                            formData.permanent_address &&
                          formData.permanent_address !== ""
                        }
                        onChange={(e) =>
                          handleSameAddressChange(e.target.checked)
                        }
                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">
                        Same as permanent address
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStaffInfo;
